import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { formatoDinero } from "../../lib/money";
import { useAuth } from "../../contexts/AuthContext";
import { T, T_EMISORES, T_HIST, T_REUSO, CLASE_ESTADO, datosDelError, descargarCfdi } from "../../lib/cfdi";
import CancelarCfdi from "./CancelarCfdi";
import { DetalleGlobal } from "./FacturaGlobal";

// Historial de facturas a pacientes (CL.4 C3). Dos usos con el MISMO componente:
//  · pestaña «Facturas a pacientes» de Facturación: filtros de fechas, paciente (nombre o RFC
//    del receptor) y estado, paginado, con descargas y la cancelación de siempre (CancelarCfdi);
//  · ficha del paciente (`patientId`): sus facturas, sin filtros (fecha, total, estado, PDF, XML).
// 🔴 El FRONTEND NO FILTRA NI CUENTA: todo va como parámetros a GET /api/cfdis, que devuelve la
//    página, el `total` y el periodo efectivo (`desde`/`hasta`). Sin fechas en la primera carga,
//    el servidor aplica el mes en curso EN LA ZONA DE LA CLÍNICA y la pantalla copia lo que dice.
// Textos: T_HIST (Copy aprobado por Jesús (5 Oct 2026)) y textos ya aprobados (lib/cfdi.js).
const campo = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-electric";
const tarjeta = "rounded-2xl border border-slate-200 bg-white p-5";
const btnSec = "rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const TIMBRADAS = ["vigente", "cancelacion_solicitada", "cancelado"];
const ORDEN_ESTADOS = ["vigente", "cancelado", "timbrando", "reservado", "error", "cancelacion_solicitada"];
const POR_PAGINA = { historial: 20, paciente: 10 };
const ESPERA_BUSQUEDA_MS = 300;

// Fecha de emisión en hora de México (como Facturación): `created_at` llega en UTC.
function fechaMx(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-MX", { timeZone: "America/Mexico_City", day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function HistorialCfdis({ patientId = null, embebido = false }) {
  const { puede } = useAuth() || {};
  const modoPaciente = patientId != null;
  const porPagina = modoPaciente ? POR_PAGINA.paciente : POR_PAGINA.historial;
  const [filtros, setFiltros] = useState({ desde: "", hasta: "", q: "", estado: "" });
  const [periodo, setPeriodo] = useState({ desde: "", hasta: "" });   // el que aplicó el servidor
  const [qEscrita, setQEscrita] = useState("");
  const [offset, setOffset] = useState(0);
  const [datos, setDatos] = useState(null);          // {cfdis, total}
  const [error, setError] = useState(null);
  const [cancelando, setCancelando] = useState(null);  // id del CFDI con el formulario abierto
  const [ocupado, setOcupado] = useState(false);
  const [errorFila, setErrorFila] = useState(null);    // {id, detail}
  const peticion = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++peticion.current;
    const params = { limit: porPagina, offset };
    if (modoPaciente) {
      params.patient_id = patientId;
    } else {
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.q) params.q = filtros.q;
      if (filtros.estado) params.estado = filtros.estado;
    }
    try {
      const { data } = await api.get("/cfdis", { params });
      if (n !== peticion.current) return;           // llegó tarde: hay una petición más nueva
      setError(null);
      setDatos({ cfdis: data?.cfdis || [], total: data?.total ?? (data?.cfdis || []).length });
      // El periodo efectivo lo decide el servidor (sin fechas: el mes en curso en la zona de la
      // clínica). Se PINTA en los campos de fecha sin volver a pedir nada.
      if (!modoPaciente) setPeriodo({ desde: data?.desde || "", hasta: data?.hasta || "" });
    } catch (e) {
      if (n !== peticion.current) return;
      setDatos({ cfdis: [], total: 0 });
      setError(datosDelError(e).detail);
    }
  }, [filtros, offset, modoPaciente, patientId, porPagina]);

  useEffect(() => { cargar(); }, [cargar]);

  // La búsqueda por nombre o RFC se manda al dejar de escribir, no en cada tecla.
  useEffect(() => {
    if (modoPaciente) return undefined;
    const t = setTimeout(() => {
      setFiltros((f) => (f.q === qEscrita.trim() ? f : { ...f, q: qEscrita.trim() }));
      setOffset(0);
    }, ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(t);
  }, [qEscrita, modoPaciente]);

  const cambiaFiltro = (k) => (e) => { setOffset(0); setFiltros((f) => ({ ...f, [k]: e.target.value })); };

  const cancelar = async (cfdi, motivo) => {
    setOcupado(true);
    setErrorFila(null);
    try {
      await api.post(`/cfdis/${encodeURIComponent(cfdi.id)}/cancelar`, { motivo });
      setCancelando(null);
      await cargar();
    } catch (e) {
      setErrorFila({ id: cfdi.id, detail: datosDelError(e).detail });
    } finally {
      setOcupado(false);
    }
  };

  const cfdis = datos?.cfdis || [];
  const total = datos?.total || 0;
  const desdeN = total === 0 ? 0 : offset + 1;
  const hastaN = Math.min(offset + cfdis.length, total);
  const vacio = modoPaciente ? T_HIST.pacienteVacio : T_HIST.vacio;

  const contenido = (
    <>
      {!modoPaciente && (
        <div className="mb-3 grid gap-3 sm:grid-cols-4" data-testid="cfdi-hist-filtros">
          <label className="text-xs font-medium text-slate-600">{T_REUSO.desde}
            <input type="date" className={campo} value={filtros.desde || periodo.desde} onChange={cambiaFiltro("desde")} data-testid="cfdi-hist-desde" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T_REUSO.hasta}
            <input type="date" className={campo} value={filtros.hasta || periodo.hasta} onChange={cambiaFiltro("hasta")} data-testid="cfdi-hist-hasta" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T_REUSO.paciente}
            <input className={campo} value={qEscrita} placeholder={T_HIST.buscarPh} maxLength={100}
                   onChange={(e) => setQEscrita(e.target.value)} data-testid="cfdi-hist-q" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T_REUSO.estado}
            <select className={campo} value={filtros.estado} onChange={cambiaFiltro("estado")} data-testid="cfdi-hist-estado">
              <option value="">{T_REUSO.todos}</option>
              {ORDEN_ESTADOS.map((e) => <option key={e} value={e}>{T.estados[e]}</option>)}
            </select>
          </label>
        </div>
      )}

      {datos === null ? null : cfdis.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="cfdi-hist-vacio">{error || vacio}</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="cfdi-hist-filas">
          {cfdis.map((c) => (
            <li key={c.id} className="py-2" data-testid={`cfdi-hist-fila-${c.id}`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-sm">
                  <span className="mr-2 text-xs text-slate-500">{fechaMx(c.created_at)}</span>
                  {c.emisor_nombre && <span className="mr-2 text-xs text-slate-600">{T_EMISORES.emisor}: {c.emisor_nombre}</span>}
                  {c.folio != null && <span className="mr-2 font-mono text-xs text-slate-500">#{c.folio}</span>}
                  <span className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${CLASE_ESTADO[c.estado] || ""}`}>{T.estados[c.estado] || c.estado}</span>
                  {!modoPaciente && <span className="text-slate-800">{c.receptor?.nombre}</span>}
                  {!modoPaciente && c.receptor?.rfc && <span className="ml-2 font-mono text-xs text-slate-500">{c.receptor.rfc}</span>}
                  <span className="ml-2 font-medium">{formatoDinero(c.total)}</span>
                  {!c.livemode && <span className="ml-2 text-xs text-amber-700">{T.prueba}</span>}
                  {c.estado === "error" && c.error_detalle && <p className="text-xs text-red-600">{c.error_detalle}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  {TIMBRADAS.includes(c.estado) && (
                    <>
                      <button type="button" className={btnSec} onClick={() => descargarCfdi(c.id, "pdf", c.uuid)} data-testid={`cfdi-hist-pdf-${c.id}`}>PDF</button>
                      <button type="button" className={btnSec} onClick={() => descargarCfdi(c.id, "xml", c.uuid)} data-testid={`cfdi-hist-xml-${c.id}`}>XML</button>
                    </>
                  )}
                  {!modoPaciente && c.estado === "vigente" && puede?.("cfdi_cancelar") && cancelando !== c.id && (
                    <button type="button" className={`${btnSec} text-red-600`} disabled={ocupado}
                            onClick={() => { setErrorFila(null); setCancelando(c.id); }} data-testid={`cfdi-hist-cancelar-${c.id}`}>
                      {T.cancelar}
                    </button>
                  )}
                </div>
              </div>
              {cancelando === c.id && (
                <div className="mt-2 rounded-xl border border-slate-200 p-3">
                  <CancelarCfdi ocupado={ocupado} onVolver={() => setCancelando(null)} onConfirmar={(motivo) => cancelar(c, motivo)} />
                </div>
              )}
              {c.es_global && <DetalleGlobal cfdi={c} onCambio={cargar} />}
              {errorFila?.id === c.id && (
                <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="cfdi-hist-error-fila">{errorFila.detail}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {total > porPagina && (
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="cfdi-hist-paginacion">
          <span data-testid="cfdi-hist-rango">{T_HIST.rango(desdeN, hastaN, total)}</span>
          <div className="flex gap-2">
            <button type="button" className={btnSec} disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - porPagina))} data-testid="cfdi-hist-anterior">{T_HIST.anterior}</button>
            <button type="button" className={btnSec} disabled={offset + porPagina >= total}
                    onClick={() => setOffset((o) => o + porPagina)} data-testid="cfdi-hist-siguiente">{T_HIST.siguiente}</button>
          </div>
        </div>
      )}
      {error && cfdis.length > 0 && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </>
  );

  if (embebido) return <div data-testid={modoPaciente ? "cfdi-paciente" : "cfdi-lista"}>{contenido}</div>;
  return (
    <div className={tarjeta} data-testid={modoPaciente ? "cfdi-paciente" : "cfdi-lista"}>
      <h3 className="mb-3 font-semibold text-slate-900">{modoPaciente ? T_HIST.pacienteTitulo : T_HIST.titulo}</h3>
      {contenido}
    </div>
  );
}
